import React, { useRef } from 'react';
import ExcelExport from "@progress/kendo-react-excel-export/dist/npm/ExcelExport";
import ExcelExportColumn from "@progress/kendo-react-excel-export/dist/es/ExcelExportColumn";
import ExcelExportColumnGroup from "@progress/kendo-react-excel-export/dist/es/ExcelExportColumnGroup";
import moment from "moment";

const PayrollCostCenterAllocationsExcelExport = ({
    allocationResults,
    payrollDate,
    region,
    department,
    site,
    payablesAllocations,
    sites
}) => {
    const ref = useRef();

    // Transform allocation results to match the visual table structure
    const getExcelData = () => {
        if (!allocationResults || !allocationResults.results) return [];

        const rows = [];

        allocationResults.results
            .filter(result => result.total !== 0)
            .forEach(result => {
                // Cost center total row
                const costCenterRow = {
                    costCenter: result.costCenter.code,
                    site: 'TOTAL',
                    isCostCenterTotal: true
                };

                // Add dimension values for cost center total
                if (result.siteAllocations.length > 0 && result.siteAllocations[0].items.length > 0) {
                    result.siteAllocations[0].items.forEach(item => {
                        item.dimensions.forEach((dim, dimIdx) => {
                            // Calculate total for this dimension across all sites
                            const totalValue = result.siteAllocations.reduce((sum, siteAlloc) => {
                                const foundItem = siteAlloc.items.find(i => i.name === item.name);
                                if (foundItem && foundItem.dimensions[dimIdx]) {
                                    return sum + foundItem.dimensions[dimIdx].value;
                                }
                                return sum;
                            }, 0);
                            costCenterRow[`${item.name}_${dim.name}`] = totalValue;
                        });
                    });
                }

                costCenterRow.total = result.total;
                rows.push(costCenterRow);

                // Site rows
                result.siteAllocations
                    .filter(siteAlloc => siteAlloc.total !== 0)
                    .forEach(siteAlloc => {
                        // Site total row
                        const siteRow = {
                            costCenter: '',
                            site: siteAlloc.site,
                            isSiteTotal: true
                        };

                        siteAlloc.items.forEach(item => {
                            item.dimensions.forEach((dim, dimIdx) => {
                                siteRow[`${item.name}_${dim.name}`] = dim.value;
                            });
                        });

                        siteRow.total = siteAlloc.total;
                        rows.push(siteRow);

                        // Employee detail rows (if expanded data is available)
                        if (siteAlloc.items.length > 0) {
                            const employeeMap = new Map();

                            siteAlloc.items.forEach(item => {
                                item.dimensions.forEach(dim => {
                                    if (dim.employees) {
                                        dim.employees.forEach(emp => {
                                            if (!employeeMap.has(emp.employeeId)) {
                                                employeeMap.set(emp.employeeId, {
                                                    orgID: emp.orgID,
                                                    fullName: emp.fullName,
                                                    allocationPercent: emp.allocationPercent,
                                                    dimensions: {}
                                                });
                                            }
                                            employeeMap.get(emp.employeeId).dimensions[`${item.name}_${dim.name}`] = emp.value;
                                        });
                                    }
                                });
                            });

                            const employees = Array.from(employeeMap.values())
                                .filter(emp => {
                                    const total = Object.values(emp.dimensions).reduce((sum, val) => sum + (val || 0), 0);
                                    return total > 0;
                                });

                            employees.forEach(emp => {
                                const empRow = {
                                    costCenter: '',
                                    site: `  ${emp.orgID} - ${emp.fullName}`,
                                    isEmployeeRow: true
                                };

                                // Add employee dimension values
                                Object.keys(emp.dimensions).forEach(key => {
                                    empRow[key] = emp.dimensions[key];
                                });

                                empRow.total = Object.values(emp.dimensions).reduce((sum, val) => sum + (val || 0), 0);
                                rows.push(empRow);
                            });
                        }
                    });
            });

        // Add grand total row
        const grandTotalRow = {
            costCenter: 'GRAND TOTAL',
            site: '',
            isGrandTotal: true
        };

        if (allocationResults.results.length > 0 && allocationResults.results[0].siteAllocations.length > 0) {
            allocationResults.results[0].siteAllocations[0].items.forEach(item => {
                item.dimensions.forEach((dim, dimIdx) => {
                    const totalValue = allocationResults.results.reduce((sum, result) => {
                        return sum + result.siteAllocations.reduce((siteSum, siteAlloc) => {
                            const foundItem = siteAlloc.items.find(i => i.name === item.name);
                            if (foundItem && foundItem.dimensions[dimIdx]) {
                                return siteSum + foundItem.dimensions[dimIdx].value;
                            }
                            return siteSum;
                        }, 0);
                    }, 0);
                    grandTotalRow[`${item.name}_${dim.name}`] = totalValue;
                });
            });
        }

        grandTotalRow.total = allocationResults.grandTotal;
        rows.push(grandTotalRow);

        return rows;
    };

    const getColumns = () => {
        if (!allocationResults || !allocationResults.results || allocationResults.results.length === 0) {
            return [];
        }

        const columns = [
            { field: 'costCenter', title: 'Cost Center', width: 150 },
            { field: 'site', title: 'Site', width: 150 }
        ];

        // Get all unique dimension columns from the first site allocation
        const firstSiteAlloc = allocationResults.results[0].siteAllocations[0];
        if (firstSiteAlloc && firstSiteAlloc.items) {
            firstSiteAlloc.items.forEach(item => {
                item.dimensions.forEach(dim => {
                    columns.push({
                        field: `${item.name}_${dim.name}`,
                        title: `${item.name} - ${dim.name}`,
                        width: 120
                    });
                });
            });
        }

        columns.push({ field: 'total', title: 'Total', width: 120 });

        return columns;
    };

    const handleExport = () => {
        const options = ref.current.workbookOptions();
        const sheet = options.sheets[0];

        sheet.frozenRows = 3;
        sheet.name = "Cost Center Allocations";

        // Add title rows
        const title = [{
            value: `Cost Center Allocations - ${moment(payrollDate).format("MMMM YYYY")}`,
            fontSize: 20,
            textAlign: "center",
            background: "#098EF6",
            color: "#ffffff",
            bold: true
        }];

        sheet.rows.splice(0, 0, { cells: title, height: 40 });
        sheet.mergedCells = ['A1:Z1'];

        // Style all rows
        sheet.rows.forEach((row, index) => {
            if (row.type === 'header') {
                row.height = 30;
                row.cells.forEach(cell => {
                    cell.borderLeft = {size: 1};
                    cell.borderRight = {size: 1};
                    cell.borderBottom = {size: 1};
                    cell.borderTop = {size: 1};
                    cell.fontSize = 12;
                    cell.background = "#BAD8FC";
                    cell.color = "#000";
                    cell.bold = true;
                });
            }
            if (row.type === 'data') {
                row.height = 25;
                row.cells.forEach(cell => {
                    cell.borderLeft = {size: 1};
                    cell.borderRight = {size: 1};
                    cell.borderBottom = {size: 1};
                    cell.borderTop = {size: 1};
                    cell.fontSize = 11;
                });
            }
        });

        ref.current.save(options);
    };

    const excelData = getExcelData();
    const columns = getColumns();

    return (
        <div style={{ marginTop: 16 }}>
            <button
                onClick={handleExport}
                style={{
                    backgroundColor: '#107C10',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                </svg>
                Export to Excel
            </button>

            <ExcelExport
                data={excelData}
                ref={ref}
                fileName={`Payday_cost_center_allocations_${moment(payrollDate).format("MMM-YYYY")}.xlsx`}
            >
                {columns.map((column, index) => (
                    <ExcelExportColumn
                        key={index}
                        field={column.field}
                        title={column.title}
                        width={column.width}
                        cellOptions={{
                            format: column.field === 'total' || column.field.includes('_') ?
                                "_(* #,##0.00_);_(* (#,##0.00);_(* \"-\"??_);_(@_)" : undefined
                        }}
                    />
                ))}
            </ExcelExport>
        </div>
    );
};

export default PayrollCostCenterAllocationsExcelExport;